import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface NewCustomerOnboardingData {
    period: string;
    period_start: string;
    total_new_customers: number;
    phone_only_customers: number;
    name_updated_customers: number;
    profile_complete_customers: number;
}

interface Props {
    data?: NewCustomerOnboardingData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const formatPeriodLabel = (date: Date, period: Props['period'], fallback: string) => {
    switch (period) {
        case 'daily':
            return format(date, 'MMM d');
        case 'weekly':
            return `W${format(date, 'II')} ${format(date, 'yyyy')}`;
        case 'monthly':
            return format(date, 'MMM yyyy');
        case 'quarterly':
            return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
        default:
            return fallback;
    }
};

const NewCustomerOnboardingChart: React.FC<Props> = ({ data = [], period }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({ series: [] });
            }
            return;
        }

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const categories: string[] = [];
            const phoneOnly: number[] = [];
            const nameUpdated: number[] = [];
            const profileComplete: number[] = [];
            const totalsByIndex: number[] = [];

            data.forEach((item) => {
                let date: Date;
                try {
                    date = item.period_start ? parseISO(item.period_start) : new Date();
                    if (isNaN(date.getTime())) {
                        date = new Date(item.period_start || '');
                    }
                } catch {
                    date = new Date();
                }

                const phoneOnlyCount = Number(item.phone_only_customers) || 0;
                const nameUpdatedCount = Number(item.name_updated_customers) || 0;
                const profileCompleteCount = Number(item.profile_complete_customers) || 0;
                const total = Number(item.total_new_customers) || phoneOnlyCount + nameUpdatedCount + profileCompleteCount;

                categories.push(formatPeriodLabel(date, period, item.period || ''));
                phoneOnly.push(phoneOnlyCount);
                nameUpdated.push(nameUpdatedCount);
                profileComplete.push(profileCompleteCount);
                totalsByIndex.push(total);
            });

            const formatBarLabel = (params: any) => {
                const total = totalsByIndex[params.dataIndex] || 0;
                const value = Number(params.value) || 0;
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                return `${value.toLocaleString()}\n${percentage}%`;
            };

            const option: ECOption = {
                tooltip: {
                    trigger: 'axis',
                    axisPointer: { type: 'shadow' },
                    formatter: (params: any) => {
                        if (!Array.isArray(params)) {
                            return '';
                        }

                        const index = params[0]?.dataIndex || 0;
                        const total = totalsByIndex[index] || 0;
                        const lines = [params[0]?.axisValue || ''];
                        params.forEach((param: any) => {
                            const value = Number(param.value) || 0;
                            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                            lines.push(`${param.marker} ${param.seriesName}: ${value.toLocaleString()} (${percentage}%)`);
                        });
                        lines.push(`Total new customers: ${total.toLocaleString()}`);
                        return lines.join('<br/>');
                    },
                },
                legend: {
                    data: ['Phone only', 'Name updated', 'Company profile complete'],
                    bottom: 0,
                },
                grid: {
                    left: '6%',
                    right: '6%',
                    top: '12%',
                    bottom: 70,
                    containLabel: true,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: { rotate: period === 'daily' ? 45 : 0 },
                },
                yAxis: {
                    type: 'value',
                    name: 'New customers',
                },
                series: [
                    {
                        name: 'Phone only',
                        type: 'bar',
                        data: phoneOnly,
                        itemStyle: { color: '#faad14' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Name updated',
                        type: 'bar',
                        data: nameUpdated,
                        itemStyle: { color: '#1890ff' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                    {
                        name: 'Company profile complete',
                        type: 'bar',
                        data: profileComplete,
                        itemStyle: { color: '#52c41a' },
                        barMaxWidth: 28,
                        label: {
                            show: true,
                            position: 'top',
                            formatter: formatBarLabel,
                            fontSize: 11,
                        },
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 380 }} />;
};

export default NewCustomerOnboardingChart;
